import { Directive, ElementRef, HostListener, Optional, Renderer2 } from '@angular/core';
import { NgModel } from '@angular/forms';
import { removeInvalidCharacters } from '../utils/string-util';

/**
 * Removes/replaces certain characters our database doesn't like.
 * Invisible/strange white space characters are removed.
 * Things that seem like bullets are replaced with a bullet symbol our database does like.
 * Things that seem like hyphens are replaced with a hyphen symbol our database does like.
 *
 * This directive should NOT be used with the CKEditor component because the editor's cursor
 * does not play nice with toolbar actions such as bold, italics, bullets, numbering, etc.
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: `
    'input[type="text"]',
    'textarea'
  `
})
export class InvalidCharacterRemoverDirective {
  private get nativeElement(): HTMLInputElement | HTMLTextAreaElement {
    return this.elementRef.nativeElement;
  }

  private get value(): string {
    if (this.ngModel != null) {
      return this.ngModel.value;
    } else {
      return this.nativeElement.value;
    }
  }

  private set value(val: string) {
    if (this.ngModel != null) {
      this.ngModel.control.setValue(val);
    } else {
      this.renderer.setProperty(this.nativeElement, 'value', val);
    }

    this.previousValue = val;
  }

  private previousValue: string;

  constructor(
    @Optional() private ngModel: NgModel,
    private elementRef: ElementRef,
    private renderer: Renderer2
  ) {}

  @HostListener('keydown.enter')
  onEnterKeyPressed(): void {
    if (!(this.nativeElement instanceof HTMLTextAreaElement)) {
      this.processEvent();
    }
  }

  @HostListener('blur')
  onBlur(): void {
    this.processEvent();
  }

  private processEvent() {
    if (this.value !== this.previousValue) {
      const newValue = removeInvalidCharacters(this.value);

      this.value = newValue;
    }
  }
}
